// Paginated dashboard for automated email follow-up obligations.

import React, { useMemo, useState } from "react";
import {
  Loader2,
  AlertCircle,
  RefreshCw,
  Mail,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  X,
  ChevronDown,
  ChevronUp,
  Send,
  Inbox,
  Clock,
  Info,
  Copy,
  Bell,
  User,
  Flag,
  Search,
  Eye,
} from "lucide-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import toast from "react-hot-toast";
import { Tooltip, DatePicker, Select as AntSelect } from "antd";
import { useQuery } from "@tanstack/react-query";
import { Button, Badge } from "../atoms";
import { crmApi } from "../api/client";
import { useAuth } from "../../../contexts/AuthContext";
import FollowUpScheduler from "../molecules/FollowUpScheduler";
import FollowUpThreadDrawer from "../overlays/FollowUpThreadDrawer";
import { useTeamMembers, formatMember, isIdentifiableMember } from "../hooks/useTeamMembers";
import {
  useEmailFollowUpObligations,
  useEmailFollowUpSummary,
  useCloseEmailFollowUp,
  useEscalateEmailFollowUp,
} from "../hooks/useFollowUps";

dayjs.extend(relativeTime);

const { RangePicker } = DatePicker;

function useDebouncedValue(value, ms = 300) {
  const [debounced, setDebounced] = useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

const COLUMN_HINTS = {
  lead: "Vendor lead name, email, and Gmail thread subject for this row.",
  owner: "Team member who owns this lead (who the follow-up is tracked against).",
  status: "Follow-up health: on track, due soon, missed, escalated, or vendor replied.",
  youSent: "When your last outbound email on this thread was sent.",
  reply: "When the vendor last replied on this thread (empty if no reply yet).",
  due: "When the next follow-up email is due. Paused after a vendor reply until you send again.",
  actions: "View the Gmail thread, copy row details, set a reminder, or flag/escalate to managers.",
};

function ColumnLabel({ label, hint }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      {label}
      <Tooltip title={hint} mouseEnterDelay={0.2}>
        <span
          role="img"
          aria-label={hint}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "help",
            color: "var(--color-text-secondary)",
            opacity: 0.75,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Info size={11} strokeWidth={2.25} />
        </span>
      </Tooltip>
    </span>
  );
}

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const SCOPE_OPTIONS = [
  { id: "mine", label: "Mine" },
  { id: "org", label: "Everyone" },
];

const statusTone = {
  missed: "danger",
  escalated: "danger",
  due_soon: "warning",
  on_track: "success",
  replied: "primary",
};

const STATUS_META = {
  on_track: { label: "On track", hint: "Waiting — follow-up not due yet" },
  due_soon: { label: "Due soon", hint: "Follow-up due within the next day" },
  missed: { label: "Missed", hint: "Due date passed with no follow-up email" },
  escalated: { label: "Escalated", hint: "Missed and flagged for manager" },
  replied: { label: "Vendor replied", hint: "Clock paused — send again to restart follow-up" },
};

const MISS_LABELS = {
  NO_FOLLOW_UP: "No follow-up sent",
  VENDOR_UNRESPONSIVE: "Vendor never replied",
  REP_LATE_AFTER_REPLY: "Late after vendor reply",
  STALE_THREAD: "Thread gone stale",
};

/** KPI card click → status filter (empty = all). */
const KPI_FILTERS = [
  { id: "missed", label: "Missed", statKey: "missed", tone: "var(--crm-danger)", bg: "var(--crm-danger-soft, #fef2f2)" },
  { id: "escalated", label: "Escalated", statKey: "escalated", tone: "var(--crm-danger)", bg: "var(--crm-danger-soft, #fef2f2)" },
  { id: "due_soon", label: "Due soon", statKey: "due_soon", tone: "var(--crm-warning, #B45309)", bg: "var(--crm-warning-soft, #fffbeb)" },
  { id: "on_track", label: "On track", statKey: "on_track", tone: "var(--crm-success)", bg: "var(--crm-success-soft, #ecfdf5)" },
  { id: "replied", label: "Replied", statKey: "replied", tone: "var(--crm-primary)", bg: "var(--crm-primary-soft)" },
  { id: "", label: "All threads", statKey: "total", tone: "var(--color-text-primary)", bg: "var(--color-bg-muted)" },
];

function formatErr(err) {
  const data = err?.response?.data;
  if (data?.error) return typeof data.error === "string" ? data.error : data.error.message;
  return err?.message || "Request failed";
}

function rel(when) {
  if (!when) return null;
  const d = dayjs(when);
  return { abs: d.format("MMM D, YYYY h:mm A"), rel: d.fromNow() };
}

// Subject lines for the rows on screen, keyed by obligation id.
//
// This used to call /leads/:id/emails once per lead — an endpoint that
// live-fetches up to 200 Gmail messages per lead. Twenty rows could mean
// thousands of Gmail round-trips per render. Now it's one request for the
// whole page, and the server does one metadata fetch per row.
function useThreadSubjectMap(items) {
  const obligationIds = useMemo(
    () => items.map((i) => i._id).filter(Boolean),
    [items]
  );
  const cacheKey = useMemo(() => [...obligationIds].sort().join(","), [obligationIds]);

  const { data } = useQuery({
    enabled: obligationIds.length > 0,
    queryKey: ["crm", "followups", "thread-subjects", cacheKey],
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: async () => {
      const res = await crmApi.post(
        "/follow-ups/obligations/thread-subjects",
        { obligationIds },
        { timeout: 45000 }
      );
      return res.data?.subjects || {};
    },
  });

  return data || {};
}

function TimelineStep({ icon: Icon, label, when, muted }) {
  const t = rel(when);
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-start", opacity: muted ? 0.45 : 1 }}>
      <Icon size={14} style={{ marginTop: 2, flexShrink: 0, color: "var(--color-text-secondary)" }} />
      <div style={{ fontSize: 12, lineHeight: 1.45 }}>
        <div style={{ fontWeight: 600, color: "var(--color-text-primary)" }}>{label}</div>
        {t ? (
          <>
            <div style={{ color: "var(--color-text-secondary)" }}>{t.rel}</div>
            <div style={{ fontSize: 11, color: "var(--color-text-secondary)", opacity: 0.85 }}>{t.abs}</div>
          </>
        ) : (
          <div style={{ color: "var(--color-text-secondary)" }}>—</div>
        )}
      </div>
    </div>
  );
}

function buildCopyText({ item, subject, ownerName }) {
  const lead = item.lead || {};
  const meta = STATUS_META[item.status] || { label: item.status };
  const sent = rel(item.lastOutboundAt);
  const reply = rel(item.lastInboundAt);
  const due = rel(item.nextDueAt);
  const lines = [
    `Lead: ${lead.title || "Lead"}`,
    lead.email ? `Email: ${lead.email}` : null,
    ownerName ? `Owner: ${ownerName}` : null,
    subject ? `Thread: ${subject}` : null,
    `Status: ${meta.label}`,
    item.missCategory ? `Miss: ${MISS_LABELS[item.missCategory] || item.missCategory}` : null,
    `You sent: ${sent ? `${sent.rel} (${sent.abs})` : "—"}`,
    `Reply: ${reply ? `${reply.rel} (${reply.abs})` : "—"}`,
    `Due: ${due ? `${due.rel} (${due.abs})` : (item.status === "replied" ? "Paused" : "—")}`,
  ].filter(Boolean);
  return lines.join("\n");
}

const actionBtnStyle = {
  height: 28,
  width: 28,
  padding: 0,
  border: "1px solid var(--color-border-subtle)",
  borderRadius: 8,
  background: "var(--color-bg-surface)",
  color: "var(--color-text-secondary)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
};

function ObligationCard({
  item, subject, expanded, onToggle, onDismiss, isOrgAdmin, onSetReminder, onViewMail,
}) {
  const close = useCloseEmailFollowUp();
  const escalate = useEscalateEmailFollowUp();
  const lead = item.lead || {};
  const meta = STATUS_META[item.status] || { label: item.status, hint: "" };
  const ownerName = item.ownerName || lead.ownerName || null;
  const canViewMail = !!(item.accountId && item.providerThreadId);
  const gridCols = isOrgAdmin
    ? "minmax(0, 1.4fr) minmax(110px, 0.9fr) minmax(120px, 1fr) 90px 90px 100px 136px 28px"
    : "minmax(0, 1.4fr) minmax(120px, 1fr) 90px 90px 100px 36px 28px";

  const handleDismiss = async (e) => {
    e.stopPropagation();
    try {
      await close.mutateAsync({ obligationId: item._id, reason: "dismissed from dashboard" });
      toast.success("Dismissed");
      onDismiss?.();
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(buildCopyText({ item, subject, ownerName }));
      toast.success("Lead details copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  const handleReminder = (e) => {
    e.stopPropagation();
    if (!item.leadId && !lead._id) {
      toast.error("Lead not found");
      return;
    }
    onSetReminder?.({
      _id: String(lead._id || item.leadId),
      title: lead.title || "Lead",
      followUp: lead.followUp || null,
    });
  };

  const handleFlag = async (e) => {
    e.stopPropagation();
    if (item.status === "escalated") {
      toast("Already escalated");
      return;
    }
    try {
      await escalate.mutateAsync({
        obligationId: item._id,
        reason: "manually flagged from email follow-ups panel",
      });
      toast.success("Flagged — managers notified");
      onDismiss?.();
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  const handleViewMail = (e) => {
    e.stopPropagation();
    if (!canViewMail) {
      toast.error("No Gmail thread linked");
      return;
    }
    onViewMail?.(item);
  };

  return (
    <div
      style={{
        borderBottom: "1px solid var(--color-border-subtle)",
        background: expanded ? "var(--color-bg-muted)" : "var(--color-bg-surface)",
        transition: "background 0.15s ease",
      }}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        style={{
          width: "100%",
          textAlign: "left",
          cursor: "pointer",
          padding: "14px 18px",
        }}
      >
        <div style={{
          display: "grid",
          gridTemplateColumns: gridCols,
          gap: 12,
          alignItems: "center",
        }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 14, color: "var(--color-text-primary)" }}>
              {lead.title || "Lead"}
            </div>
            {lead.email && (
              <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 2 }}>
                {lead.email}
              </div>
            )}
            {subject ? (
              <div style={{
                fontSize: 12,
                color: "var(--crm-primary)",
                marginTop: 4,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              >
                <Mail size={11} style={{ verticalAlign: "middle", marginRight: 4 }} />
                {subject}
              </div>
            ) : item.providerThreadId ? (
              <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 4 }}>
                Expand for timeline
              </div>
            ) : null}
          </div>

          {isOrgAdmin && (
            <div style={{ minWidth: 0 }}>
              <div style={{
                fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                letterSpacing: "0.06em", marginBottom: 3, color: "var(--color-text-secondary)",
              }}
              >
                Owner
              </div>
              <div style={{
                display: "flex", alignItems: "center", gap: 5, fontSize: 12,
                fontWeight: 500, color: "var(--color-text-primary)",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}
              >
                <User size={12} style={{ flexShrink: 0, color: "var(--color-text-secondary)" }} />
                {ownerName || "—"}
              </div>
            </div>
          )}

          <div>
            <Badge tone={statusTone[item.status] || "neutral"}>{meta.label}</Badge>
            {item.missCategory && (
              <div style={{ fontSize: 11, color: "var(--crm-danger)", marginTop: 4 }}>
                {MISS_LABELS[item.missCategory] || item.missCategory}
              </div>
            )}
          </div>

          <TimeCell label="You sent" when={item.lastOutboundAt} />
          <TimeCell label="Reply" when={item.lastInboundAt} />
          <TimeCell
            label="Due"
            when={item.nextDueAt}
            fallback={item.status === "replied" ? "Paused" : "—"}
          />

          {isOrgAdmin && (
            <div
              style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              role="presentation"
            >
              <Tooltip title={canViewMail ? "View email thread" : "No Gmail thread linked"} mouseEnterDelay={0.2}>
                <button
                  type="button"
                  style={{
                    ...actionBtnStyle,
                    opacity: canViewMail ? 1 : 0.45,
                    color: canViewMail ? "var(--crm-primary)" : "var(--color-text-secondary)",
                  }}
                  aria-label="View email thread"
                  onClick={handleViewMail}
                  disabled={!canViewMail}
                >
                  <Eye size={13} />
                </button>
              </Tooltip>
              <Tooltip title="Copy lead details" mouseEnterDelay={0.2}>
                <button
                  type="button"
                  style={actionBtnStyle}
                  aria-label="Copy lead details"
                  onClick={handleCopy}
                >
                  <Copy size={13} />
                </button>
              </Tooltip>
              <Tooltip title="Set reminder on this lead" mouseEnterDelay={0.2}>
                <button
                  type="button"
                  style={actionBtnStyle}
                  aria-label="Set reminder"
                  onClick={handleReminder}
                >
                  <Bell size={13} />
                </button>
              </Tooltip>
              <Tooltip
                title={item.status === "escalated" ? "Already escalated" : "Flag / escalate now — notify managers"}
                mouseEnterDelay={0.2}
              >
                <button
                  type="button"
                  style={{
                    ...actionBtnStyle,
                    opacity: item.status === "escalated" || escalate.isPending ? 0.45 : 1,
                    color: item.status === "escalated" ? "var(--crm-danger)" : "var(--color-text-secondary)",
                  }}
                  aria-label="Flag escalate"
                  onClick={handleFlag}
                  disabled={item.status === "escalated" || escalate.isPending}
                >
                  <Flag size={13} />
                </button>
              </Tooltip>
            </div>
          )}

          {!isOrgAdmin && (
            <div
              style={{ display: "flex", justifyContent: "center" }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              role="presentation"
            >
              <Tooltip title={canViewMail ? "View email thread" : "No Gmail thread linked"} mouseEnterDelay={0.2}>
                <button
                  type="button"
                  style={{
                    ...actionBtnStyle,
                    opacity: canViewMail ? 1 : 0.45,
                    color: canViewMail ? "var(--crm-primary)" : "var(--color-text-secondary)",
                  }}
                  aria-label="View email thread"
                  onClick={handleViewMail}
                  disabled={!canViewMail}
                >
                  <Eye size={13} />
                </button>
              </Tooltip>
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "center", color: "var(--color-text-secondary)" }}>
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
        </div>
      </div>

      {expanded && (
        <div style={{ padding: "0 18px 16px", display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: 14,
            padding: 14,
            borderRadius: 10,
            background: "var(--color-bg-base)",
            border: "1px solid var(--color-border-subtle)",
          }}
          >
            <TimelineStep icon={Send} label="Your last outbound" when={item.lastOutboundAt} />
            <TimelineStep icon={Inbox} label="Vendor last reply" when={item.lastInboundAt} />
            <TimelineStep icon={Clock} label="Follow-up due" when={item.nextDueAt} muted={item.status === "replied"} />
          </div>
          <p style={{ margin: 0, fontSize: 12, color: "var(--color-text-secondary)", lineHeight: 1.5 }}>
            {meta.hint}
            {item.replyTatHours != null && <span> · Reply TAT {item.replyTatHours}h</span>}
            {item.followUpTatHours != null && <span> · Follow-up TAT {item.followUpTatHours}h</span>}
          </p>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button variant="ghost" size="sm" leftIcon={<X size={12} />} onClick={handleDismiss} disabled={close.isPending}>
              Dismiss tracking
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function TimeCell({ label, when, fallback = "—" }) {
  const t = rel(when);
  return (
    <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
      <div style={{
        fontSize: 10, fontWeight: 700, textTransform: "uppercase",
        letterSpacing: "0.06em", marginBottom: 3, color: "var(--color-text-secondary)",
      }}
      >
        {label}
      </div>
      {t ? (
        <>
          <div style={{ fontWeight: 500, color: "var(--color-text-primary)" }}>{t.rel}</div>
          <div style={{ fontSize: 10, opacity: 0.8 }}>{t.abs}</div>
        </>
      ) : (
        <span style={{ fontStyle: fallback === "Paused" ? "italic" : "normal" }}>{fallback}</span>
      )}
    </div>
  );
}

function PaginationBar({
  page, pageCount, pageSize, total, skip, onPage, onPageSize,
}) {
  const pages = useMemo(() => {
    if (pageCount <= 0) return [];
    if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i);
    const set = new Set([0, pageCount - 1, page]);
    if (page > 0) set.add(page - 1);
    if (page < pageCount - 1) set.add(page + 1);
    return [...set].sort((a, b) => a - b);
  }, [page, pageCount]);

  if (total <= 0) return null;

  const from = skip + 1;
  const to = Math.min(skip + pageSize, total);

  const btn = (disabled) => ({
    height: 32,
    minWidth: 32,
    padding: "0 8px",
    border: "1px solid var(--color-border-subtle)",
    borderRadius: 8,
    background: "var(--color-bg-surface)",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.4 : 1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 500,
    color: "var(--color-text-primary)",
  });

  return (
    <div style={{
      padding: "12px 24px",
      borderTop: "1px solid var(--color-border-subtle)",
      background: "var(--color-bg-surface)",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "wrap",
    }}
    >
      <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
        Showing <strong style={{ color: "var(--color-text-primary)" }}>{from}–{to}</strong> of{" "}
        <strong style={{ color: "var(--color-text-primary)" }}>{total}</strong> threads
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <label style={{ fontSize: 12, color: "var(--color-text-secondary)", display: "flex", alignItems: "center", gap: 6 }}>
          Per page
          <select
            value={pageSize}
            onChange={(e) => onPageSize(Number(e.target.value))}
            style={{
              height: 32,
              padding: "0 8px",
              borderRadius: 8,
              border: "1px solid var(--color-border-subtle)",
              background: "var(--color-bg-base)",
              fontSize: 12,
              color: "var(--color-text-primary)",
            }}
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>

        <button type="button" style={btn(page === 0)} disabled={page === 0} onClick={() => onPage(0)} aria-label="First page">
          <ChevronsLeft size={14} />
        </button>
        <button type="button" style={btn(page === 0)} disabled={page === 0} onClick={() => onPage(page - 1)} aria-label="Previous page">
          <ChevronLeft size={14} />
        </button>

        {pages.map((p, i) => {
          const prev = pages[i - 1];
          const gap = prev != null && p - prev > 1;
          return (
            <React.Fragment key={p}>
              {gap && <span style={{ fontSize: 12, color: "var(--color-text-secondary)", padding: "0 2px" }}>…</span>}
              <button
                type="button"
                style={{
                  ...btn(false),
                  background: p === page ? "var(--crm-primary)" : "var(--color-bg-surface)",
                  color: p === page ? "#fff" : "var(--color-text-primary)",
                  borderColor: p === page ? "var(--crm-primary)" : "var(--color-border-subtle)",
                }}
                onClick={() => onPage(p)}
              >
                {p + 1}
              </button>
            </React.Fragment>
          );
        })}

        <button type="button" style={btn(page >= pageCount - 1)} disabled={page >= pageCount - 1} onClick={() => onPage(page + 1)} aria-label="Next page">
          <ChevronRight size={14} />
        </button>
        <button type="button" style={btn(page >= pageCount - 1)} disabled={page >= pageCount - 1} onClick={() => onPage(pageCount - 1)} aria-label="Last page">
          <ChevronsRight size={14} />
        </button>
      </div>
    </div>
  );
}

function EmptyState({ status, scope }) {
  const statusLabel = status ? STATUS_META[status]?.label || status : null;
  return (
    <div style={{
      margin: 24,
      padding: "48px 32px",
      textAlign: "center",
      borderRadius: 12,
      border: "1px dashed var(--color-border-subtle)",
      background: "var(--color-bg-surface)",
    }}
    >
      <div style={{
        width: 48, height: 48, borderRadius: 12, margin: "0 auto 16px",
        background: "var(--crm-primary-soft)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
      >
        <Mail size={22} style={{ color: "var(--crm-primary)", opacity: 0.8 }} />
      </div>
      <div style={{ fontSize: 15, fontWeight: 600, color: "var(--color-text-primary)", marginBottom: 6 }}>
        {statusLabel ? `No ${statusLabel.toLowerCase()} threads` : "No email threads tracked yet"}
      </div>
      <p style={{ margin: "0 0 20px", fontSize: 13, color: "var(--color-text-secondary)", lineHeight: 1.55, maxWidth: 420, marginInline: "auto" }}>
        {scope === "mine" && !status
          ? "Try switching to Everyone, or sync Gmail on a lead with a matching vendor email."
          : "Connect Gmail in Settings, then send or sync mail on a lead."}
      </p>
      <ol style={{
        textAlign: "left",
        margin: "0 auto",
        paddingLeft: 20,
        maxWidth: 360,
        fontSize: 13,
        color: "var(--color-text-secondary)",
        lineHeight: 1.7,
      }}
      >
        <li>Settings → Follow-ups → turn on email monitoring</li>
        <li>Settings → Email → connect Gmail</li>
        <li>Open a lead → send email or tap Sync on Emails tab</li>
      </ol>
    </div>
  );
}

export default function EmailFollowUpsPanel() {
  const { user } = useAuth();
  const isOrgAdmin = user?.role === "org_admin" || user?.role === "main_admin";
  const { members } = useTeamMembers();
  const [scope, setScope] = useState("mine");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [expandedId, setExpandedId] = useState(null);
  const [showInfo, setShowInfo] = useState(true);
  const [reminderLead, setReminderLead] = useState(null);
  const [threadItem, setThreadItem] = useState(null);
  const [search, setSearch] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);

  const skip = page * pageSize;
  const { data, isLoading, isError, error, refetch, isFetching } = useEmailFollowUpObligations({
    scope,
    status: status || undefined,
    limit: pageSize,
    skip,
    q: debouncedSearch,
    ownerId: ownerId || undefined,
    from: from || undefined,
    to: to || undefined,
  });
  const { data: summaryData } = useEmailFollowUpSummary({ scope });

  const items = data?.items || [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const summary = summaryData?.summary || {};
  const subjectMap = useThreadSubjectMap(items);
  const headerCols = isOrgAdmin
    ? "minmax(0, 1.4fr) minmax(110px, 0.9fr) minmax(120px, 1fr) 90px 90px 100px 136px 28px"
    : "minmax(0, 1.4fr) minmax(120px, 1fr) 90px 90px 100px 36px 28px";

  const onChangeScope = (id) => { setScope(id); setPage(0); setExpandedId(null); };
  const onChangeStatus = (id) => { setStatus(id); setPage(0); setExpandedId(null); };
  const onPageSize = (size) => { setPageSize(size); setPage(0); setExpandedId(null); };
  const onChangeOwner = (v) => { setOwnerId(v || ""); setPage(0); setExpandedId(null); };
  const onChangeRange = (dates) => {
    setFrom(dates?.[0] ? dates[0].format("YYYY-MM-DD") : "");
    setTo(dates?.[1] ? dates[1].format("YYYY-MM-DD") : "");
    setPage(0);
    setExpandedId(null);
  };
  const rangeValue = from || to ? [from ? dayjs(from) : null, to ? dayjs(to) : null] : null;
  const hasExtraFilters = !!(search || ownerId || from || to);
  const clearExtraFilters = () => {
    setSearch("");
    setOwnerId("");
    setFrom("");
    setTo("");
    setPage(0);
    setExpandedId(null);
  };

  React.useEffect(() => {
    setPage(0);
    setExpandedId(null);
  }, [debouncedSearch]);

  const kpiValue = (k) => summary[k.statKey] || 0;
  const ownerOptions = useMemo(
    () => (members || []).filter(isIdentifiableMember).map((m) => ({
      value: m._id,
      label: formatMember(m),
    })),
    [members]
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {showInfo && (
        <div style={{
          margin: "0 24px 16px",
          padding: "12px 14px",
          borderRadius: 10,
          background: "var(--crm-primary-soft)",
          border: "1px solid var(--color-border-subtle)",
          display: "flex",
          gap: 10,
          alignItems: "flex-start",
          fontSize: 12,
          lineHeight: 1.5,
          color: "var(--color-text-secondary)",
        }}
        >
          <Info size={16} style={{ flexShrink: 0, marginTop: 1, color: "var(--crm-primary)" }} />
          <div style={{ flex: 1 }}>
            <strong style={{ color: "var(--color-text-primary)" }}>One row = one Gmail thread on a lead.</strong>
            {" "}Tracking starts when matched mail syncs — no manual logging. Vendor reply pauses the clock until you send again.
          </div>
          <button
            type="button"
            onClick={() => setShowInfo(false)}
            aria-label="Dismiss"
            style={{ border: "none", background: "transparent", cursor: "pointer", color: "var(--color-text-secondary)", padding: 2 }}
          >
            <X size={14} />
          </button>
        </div>
      )}

      <div style={{ padding: "0 24px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          {SCOPE_OPTIONS.map((opt) => (
            <button key={opt.id} type="button" onClick={() => onChangeScope(opt.id)} style={{
              height: 32, padding: "0 14px", borderRadius: 999, border: "1px solid",
              borderColor: scope === opt.id ? "var(--crm-primary)" : "var(--color-border-subtle)",
              background: scope === opt.id ? "var(--crm-primary)" : "var(--color-bg-surface)",
              color: scope === opt.id ? "#fff" : "var(--color-text-secondary)",
              fontSize: 13, fontWeight: scope === opt.id ? 600 : 500, cursor: "pointer",
            }}
            >
              {opt.label}
            </button>
          ))}
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            height: 32, padding: "0 10px", minWidth: 200, flex: "1 1 200px", maxWidth: 280,
            borderRadius: 8, border: "1px solid var(--color-border-subtle)",
            background: "var(--color-bg-surface)",
          }}
          >
            <Search size={14} style={{ color: "var(--color-text-secondary)", flexShrink: 0 }} />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search lead, email…"
              aria-label="Search threads"
              style={{
                flex: 1, border: "none", outline: "none", background: "transparent",
                fontSize: 13, color: "var(--color-text-primary)", minWidth: 0,
              }}
            />
            {search ? (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Clear search"
                style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, color: "var(--color-text-secondary)", display: "inline-flex" }}
              >
                <X size={14} />
              </button>
            ) : null}
          </div>
          <AntSelect
            value={ownerId || undefined}
            onChange={(v) => onChangeOwner(v || "")}
            placeholder="All owners"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ minWidth: 180, height: 32 }}
            options={ownerOptions}
            aria-label="Filter by owner"
          />
          <RangePicker
            value={rangeValue}
            onChange={onChangeRange}
            format="DD MMM YYYY"
            placeholder={["Due from", "Due to"]}
            style={{ height: 32 }}
            allowEmpty={[true, true]}
          />
          {hasExtraFilters && (
            <button
              type="button"
              onClick={clearExtraFilters}
              style={{
                height: 32, padding: "0 10px", borderRadius: 8,
                border: "1px solid var(--color-border-subtle)",
                background: "var(--color-bg-surface)",
                color: "var(--color-text-secondary)",
                fontSize: 12, cursor: "pointer",
                display: "inline-flex", alignItems: "center", gap: 4,
              }}
            >
              <X size={12} /> Clear filters
            </button>
          )}
        </div>
        <Button variant="secondary" size="sm" leftIcon={<RefreshCw size={13} className={isFetching ? "animate-spin" : ""} />} onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? "Refreshing…" : "Refresh"}
        </Button>
      </div>

      <div style={{
        padding: "0 24px 16px",
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
        gap: 10,
      }}
      >
        {KPI_FILTERS.map((k) => {
          const active = status === k.id;
          const value = kpiValue(k);
          return (
            <button
              key={k.id || "all"}
              type="button"
              onClick={() => onChangeStatus(k.id)}
              style={{
                textAlign: "left",
                padding: "12px 14px",
                borderRadius: 10,
                border: `2px solid ${active ? "var(--crm-primary)" : "var(--color-border-subtle)"}`,
                background: active ? "var(--color-bg-surface)" : k.bg,
                cursor: "pointer",
                transition: "border-color 0.15s ease",
              }}
            >
              <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-secondary)" }}>
                {k.label}
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: k.tone, marginTop: 4, fontVariantNumeric: "tabular-nums" }}>
                {value}
              </div>
            </button>
          );
        })}
      </div>

      <div style={{
        flex: 1,
        minHeight: 0,
        margin: "0 24px",
        borderRadius: 12,
        border: "1px solid var(--color-border-subtle)",
        background: "var(--color-bg-surface)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
      >
        <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
          {isLoading && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 48, justifyContent: "center", color: "var(--color-text-secondary)" }}>
              <Loader2 size={18} className="animate-spin" /> Loading threads…
            </div>
          )}
          {isError && (
            <div style={{ background: "var(--crm-danger-soft)", color: "var(--crm-danger)", padding: 16, borderRadius: 10, margin: 16 }}>
              <AlertCircle size={18} style={{ verticalAlign: "middle", marginRight: 6 }} />
              {formatErr(error)}
            </div>
          )}
          {!isLoading && !isError && items.length === 0 && (
            <EmptyState status={status} scope={scope} />
          )}
          {!isLoading && !isError && items.length > 0 && (
            <>
              <div style={{
                display: "grid",
                gridTemplateColumns: headerCols,
                gap: 12,
                padding: "10px 18px",
                fontSize: 10,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: "var(--color-text-secondary)",
                borderBottom: "1px solid var(--color-border-subtle)",
                background: "var(--color-bg-muted)",
                position: "sticky",
                top: 0,
                zIndex: 1,
              }}
              >
                <ColumnLabel label="Lead / thread" hint={COLUMN_HINTS.lead} />
                {isOrgAdmin && <ColumnLabel label="Owner" hint={COLUMN_HINTS.owner} />}
                <ColumnLabel label="Status" hint={COLUMN_HINTS.status} />
                <ColumnLabel label="You sent" hint={COLUMN_HINTS.youSent} />
                <ColumnLabel label="Reply" hint={COLUMN_HINTS.reply} />
                <ColumnLabel label="Due" hint={COLUMN_HINTS.due} />
                {isOrgAdmin && <ColumnLabel label="Actions" hint={COLUMN_HINTS.actions} />}
                {!isOrgAdmin && <span />}
                <span />
              </div>
              {items.map((item) => (
                <ObligationCard
                  key={item._id}
                  item={item}
                  subject={subjectMap[item._id]}
                  expanded={expandedId === item._id}
                  onToggle={() => setExpandedId((id) => (id === item._id ? null : item._id))}
                  onDismiss={() => refetch()}
                  isOrgAdmin={isOrgAdmin}
                  onSetReminder={setReminderLead}
                  onViewMail={setThreadItem}
                />
              ))}
            </>
          )}
        </div>

        <PaginationBar
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          total={total}
          skip={skip}
          onPage={(p) => { setPage(p); setExpandedId(null); }}
          onPageSize={onPageSize}
        />
      </div>

      {isOrgAdmin && (
        <FollowUpScheduler
          open={Boolean(reminderLead)}
          lead={reminderLead}
          onClose={() => setReminderLead(null)}
        />
      )}

      <FollowUpThreadDrawer
        open={Boolean(threadItem)}
        item={threadItem}
        onClose={() => setThreadItem(null)}
      />
    </div>
  );
}
